/**
 * size-picker-pdp.js
 *
 * Handles the NH Size Picker PDP block client-side behaviour:
 *  - Update "Selected:" label on variant change
 *  - Show legacy notify banner on sold-out selection (when no delivery bar)
 *  - Roving tabindex + arrow key navigation between pills (WCAG 2.0 AA)
 *  - Re-run all of the above after variant-picker.js morphs the DOM
 *  - Wire [data-nhsg-open-chat] buttons to window.FrontChat
 *
 * Native <input type="radio"> handles arrow key navigation between inputs
 * in the same name group. We augment this with roving tabindex management
 * on the visible pill labels, keeping focus visible and predictable for
 * keyboard users navigating via Tab as well as arrows.
 */

class NHSizeGuide extends HTMLElement {
  connectedCallback() {
    this.#setupChangeListener();
    this.#setupRovingTabindex();
    this.#setupChatButtons();
    this.#setupVariantUpdateListener();
    this.#setupMorphObserver();
  }

  // ─── Variant change ────────────────────────────────────────────────────────

  #setupChangeListener() {
    this.addEventListener('change', (e) => {
      if (!(e.target instanceof HTMLInputElement) || e.target.type !== 'radio') return;

      const isAvailable = e.target.dataset.available !== 'false';
      this.#updateSelectedDisplay(e.target.value);
      this.#updateRovingTabindex(e.target);

      // Delivery bar handles OOS messaging when present
      if (this.querySelector('nh-delivery-bar')) return;

      if (!isAvailable) {
        const pill      = e.target.nextElementSibling;
        const primary   = pill?.querySelector('.nhsg__pill-primary')?.textContent?.trim()   ?? '';
        const secondary = pill?.querySelector('.nhsg__pill-secondary')?.textContent?.trim() ?? '';
        const label     = secondary ? primary + ' (' + secondary + ')' : primary;
        // Read translatable string from data attribute set in Liquid
        const soldOutTpl = this.dataset.i18nSoldOut || 'Size {size} is sold out';
        this.#showNotify(soldOutTpl.replace('{size}', label));
      } else {
        this.#hideNotify();
      }
    });
  }

  // ─── Roving tabindex ───────────────────────────────────────────────────────

  /**
   * Spec requirement: active pill gets tabindex="0", all others get tabindex="-1".
   * Arrow keys move between pills. This is applied to the visible label elements
   * so keyboard users see a visible focus ring on the pill itself.
   *
   * Native radio inputs already handle arrow key navigation, so we only need to
   * sync the tabindex state on the labels and ensure focus moves to the new label
   * when an arrow key changes the radio selection.
   */
  #setupRovingTabindex() {
    this.querySelectorAll('.nhsg__picker-fieldset').forEach((fieldset) => {
      const inputs = Array.from(fieldset.querySelectorAll('.nhsg__pill-input'));
      const labels = Array.from(fieldset.querySelectorAll('.nhsg__pill'));

      // Set initial tabindex state
      inputs.forEach((input, i) => {
        const label = labels[i];
        if (!label) return;
        if (input.checked) {
          label.setAttribute('tabindex', '0');
        } else {
          label.setAttribute('tabindex', '-1');
        }
      });

      // Arrow key navigation on the fieldset
      fieldset.addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
        e.preventDefault();

        const currentInput = /** @type {HTMLInputElement | null} */ (
          fieldset.querySelector('.nhsg__pill-input:checked, .nhsg__pill-input:focus')
        );
        if (!currentInput) return;

        const currentIndex = inputs.indexOf(currentInput);
        const dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
        const nextIndex = (currentIndex + dir + inputs.length) % inputs.length;
        const nextInput = inputs[nextIndex];
        const nextLabel = labels[nextIndex];

        if (!nextInput || !nextLabel) return;

        // Skip disabled — but per spec OOS uses aria-disabled not disabled,
        // so they ARE navigable. Skip truly disabled inputs only.
        nextInput.checked = true;
        nextInput.dispatchEvent(new Event('change', { bubbles: true }));
        nextLabel.focus();
      });

      // When a label gets focus (via Tab), make its input checked if not already
      labels.forEach((label, i) => {
        label.addEventListener('focus', () => {
          const input = inputs[i];
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // Enter/Space activate the focused label
        label.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const input = inputs[i];
            if (input) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      });
    });
  }

  /**
   * Update roving tabindex after a selection change.
   * @param {HTMLInputElement} activeInput
   */
  #updateRovingTabindex(activeInput) {
    const fieldset = activeInput.closest('.nhsg__picker-fieldset');
    if (!fieldset) return;

    const inputs = Array.from(fieldset.querySelectorAll('.nhsg__pill-input'));
    const labels = Array.from(fieldset.querySelectorAll('.nhsg__pill'));

    inputs.forEach((input, i) => {
      const label = labels[i];
      if (!label) return;
      label.setAttribute('tabindex', input === activeInput ? '0' : '-1');
    });
  }

  // ─── Variant title formatting ──────────────────────────────────────────────

  /** @param {string} title @returns {[string, string | null]} */
  #splitTitle(title) {
    if (title.includes(' | ')) return title.split(' | ', 2).map((s) => s.trim());
    if (title.includes(' - ')) return title.split(' - ', 2).map((s) => s.trim());
    if (title.includes(' / ')) return title.split(' / ', 2).map((s) => s.trim());
    return [title, null];
  }

  /** @param {string} title @returns {string} */
  #formatDisplay(title) {
    if (!title) return title;
    const [partA, partB] = this.#splitTitle(title);
    if (!partB) return partA;

    const isRing   = this.dataset.isRing   === 'true';
    const isBangle = this.dataset.isBangle === 'true';

    if (isRing) {
      const frPart = [partA, partB].find((p) => /\(FR\)/i.test(p));
      const usPart = [partA, partB].find((p) => /\(US\)/i.test(p));
      if (frPart && usPart) {
        const frNum = frPart.replace(/\s*\(FR\)/i, '').trim();
        const usNum = usPart.replace(/\s*\(US\)/i, '').trim();
        return 'FR ' + frNum + ' (US ' + usNum + ')';
      }
      return partA + ' (' + partB + ')';
    }
    if (isBangle) {
      const cmPart   = [partA, partB].find((p) => /cm/i.test(p));
      const sizePart = [partA, partB].find((p) => !/cm/i.test(p));
      if (cmPart && sizePart) return sizePart + ' (' + cmPart + ')';
      return partA + ' (' + partB + ')';
    }
    return partA + ' (' + partB + ')';
  }

  /** @param {string} title */
  #updateSelectedDisplay(title) {
    const el = this.querySelector('[data-nhsg-selected]');
    if (el) el.textContent = this.#formatDisplay(title);
  }

  // ─── Legacy notify bar (when no delivery bar) ──────────────────────────────

  /** @param {string} message */
  #showNotify(message) {
    const bar    = this.querySelector('.nhsg__notify-bar');
    const textEl = bar?.querySelector('[data-nhsg-notify-text]');
    if (!bar) return;
    if (textEl) textEl.textContent = message;
    bar.hidden = false;
  }

  #hideNotify() {
    const bar = this.querySelector('.nhsg__notify-bar');
    if (bar) bar.hidden = true;
  }

  // ─── FrontChat buttons ─────────────────────────────────────────────────────

  #setupChatButtons() {
    this.querySelectorAll('[data-nhsg-open-chat]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (typeof window.FrontChat === 'function') window.FrontChat('show');
      });
    });
  }

  // ─── Variant update listener ───────────────────────────────────────────────

  /**
   * The theme dispatches 'variant:update' on the section element after every
   * variant selection — AFTER the morph is complete. This is the authoritative
   * moment to sync the Selected: label and roving tabindex.
   * event.detail.resource is the full variant object; .title is the raw variant
   * title string (e.g. "49 (FR) | 4.75 (US)") which #formatDisplay processes.
   */
  #setupVariantUpdateListener() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.addEventListener('variant:update', (e) => {
      const variant = e.detail && (e.detail.resource || e.detail.variant);
      if (!variant) return;

      // Update Selected: label using the variant title
      this.#updateSelectedDisplay(variant.title || '');

      // Re-sync roving tabindex to the now-checked pill
      const picker  = this.querySelector('variant-picker');
      if (!picker) return;
      const checked =
        picker.querySelector('input[type="radio"][data-current-checked="true"]') ??
        picker.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement) {
        this.#updateRovingTabindex(checked);
      }

      // Re-wire chat buttons (morph may have replaced them)
      this.#setupChatButtons();
    });
  }

  // ─── Morph observer (roving tabindex + chat only) ─────────────────────────

  /**
   * MutationObserver handles DOM changes that don't trigger variant:update
   * (e.g. theme editor live preview morphs). Only re-syncs tabindex and
   * chat buttons — Selected: label is handled by variant:update above.
   */
  #setupMorphObserver() {
    const picker = this.querySelector('variant-picker');
    if (!picker) return;

    const observer = new MutationObserver(() => {
      this.#setupRovingTabindex();
      this.#setupChatButtons();
    });

    observer.observe(picker, {
      childList: true,
      subtree: true,
    });
  }
}

if (!customElements.get('nh-size-guide')) {
  customElements.define('nh-size-guide', NHSizeGuide);
}